<template>
	<div class="rel">

		<!-- Default settings -->
		<template v-if="viewDefaultSettings">
			<h2 class="modal-title">Default Rolling Availability Settings</h2>
			<AvailabilitySettings context="dates"  @back="viewDefaultSettings = false" @close="$emit('close')"/>
		</template>
		
		<!-- Edit -->
		<AvailabilityEdit v-else-if="editMode" @close="editMode = false"/>
		
		<!-- Dates -->
		<template v-else>
			<h2 class="fs-22 lh-24 m0">Edit Calendar</h2>
			<p class="mt-8 fs-14 lh-22" style="max-width:600px">
				Click any date or group of dates to change your availability, roles, rates, or travel distance. Make sure you've set the specific dates you're NOT available to avoid declining too many <span class="no-wrap">jobs.
				<span class="rel" style="vertical-align: sub;">
					<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none">
						<path stroke="#004ADB" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12Z" />
						<path fill="#140047" d="M7.5 7.5H8V11h.5" />
						<path stroke="#000" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7.5 7.5H8V11h.5" />
						<path fill="#140047" d="M8 6a.75.75 0 1 0 0-1.5A.75.75 0 0 0 8 6Z" />
					</svg>
					<Tooltip arrow="bottom center">Your default rates will be applied to all dates as defined in your Default Rolling Availability, except dates where you've customized availability settings.</Tooltip>
				</span></span>
			</p>

			<div class="px-24 pt-24 pb-32 border br-16">
				<div class="flex-col align-center gap-16 lg-flex-row lg-align-start lg-justify-left">
					<button class="btn btn-link lg-mt-16 lg-order-10 lg-align-right" @click="viewDefaultSettings = true">Edit Default Availability</button>
					<div class="flex-col align-center lg-align-start">
						<div class="flex-col flex-center sm-flex-row wrap gap-16">
							<button class="btn px-16" :disabled="!selectedDates.length" @click="editMode = true">Edit Selected Dates</button>
							<button class="btn px-16" :disabled="!canSetUnavailable.length || canSetAvailable.length > 0" @click="saveUnavailable">Set {{ canSetUnavailable.length }} to Unavailable</button>
							<button class="btn px-16" :disabled="!canSetAvailable.length || canSetUnavailable.length > 0" @click="saveAvailable">Set {{ canSetAvailable.length }} to Available</button>
						</div>
						<div class="fs-14 mt-12">{{ selectedDates.length }} {{ selectedDates.length == 1 ? 'date' : 'dates' }} selected</div>
					</div>
				</div>
				<DoubleAvailabilityCalendar class="mt-48"/>
			</div>

			<!-- todo: make global component? -->
			<div v-if="errorMessage || successMessage" :class="{'error-message': errorMessage, 'success-message': successMessage}" class="mt-24 abs left-24 right-24 bottom-16 bg-fff">
				{{ errorMessage ? errorMessage : successMessage }}
			</div>
		</template>
	</div>
</template>

<script>
import helpers from '@/mixins/helpers';
import formHelpers from '@/mixins/formHelpers';
import DoubleAvailabilityCalendar from '@/components/calendars/DoubleAvailabilityCalendar';
import AvailabilityEdit from '@/components/availability/AvailabilityEdit';
import AvailabilitySettings from '@/components/availability/AvailabilitySettings';
import { request } from '@/plugins/axios';

export default {
	name: 'AvailabilityDates',
	mixins: [helpers, formHelpers],
	props: {
		context: [String, null],
	},
	components: {
		DoubleAvailabilityCalendar,
		AvailabilityEdit,
		AvailabilitySettings,
	},
	data() {
		return {
			editMode: false,
			viewDefaultSettings: false,
		};
	},
	computed: {
		defaultAvailability() {
			return this.$store.getters['availability/defaultAvailability']
		},
		defaultAvailabilityConfig() {
			let defaults = this.$store.getters['availability/defaultAvailability'];
			let out = [];
			defaults.list.forEach((av) => {
				const obj = {
					positionId: av.roleId,
					ratePerHour: av.ratePerHour,
					withinMiles: av.withinMiles,
				};
				out.push(obj);
			});

			return out;
		},
		selectedDates() {
			return this.$store.getters['availability/selectedDates'];
		},
		canSetUnavailable() {
			return this.selectedDates.filter(date => {
				return date.availabilityRates.length;
			});
		},
		canSetAvailable() {
			return this.selectedDates.filter(date => {
				return !date.availabilityRates.length;
			});
		},
	},
	methods: {
		saveUnavailable() {
			if (this.isProcessing) return;
			this.isProcessing = true;
			const dateList = [];
			this.selectedDates.forEach(date => {
				dateList.push(date.dateString);
			});

			request({
				url: '/availabilities/v2/dates/multiple',
				method: 'put',
				data: {
					dateList,
					availableAs: [],
					status: 'unavailable',
				},
			}).then(() => {
				this.refreshAvailability();
				this.onSuccess();
			}).catch((error) => {
				this.isProcessing = false;
				this.errorMessage = this.getErrorMessage(error);
			});
		},
		saveAvailable() {
			this.isProcessing = true;
			const dateList = [];
			this.selectedDates.forEach(date => {
				dateList.push(date.dateString);
			});

			return request({
				url: '/availabilities/v2/dates/multiple',
				method: 'put',
				data: {
					dateList,
					availableAs: this.defaultAvailabilityConfig,
					withinZip: this.defaultAvailability.withinZip,
					status: 'available',
				},
			}).then(() => {
				this.refreshAvailability();
				this.onSuccess();
			}).catch((error) => {
				this.isProcessing = false;
				this.errorMessage = this.getErrorMessage(error);
			});
		},
		onSuccess() {
			this.isProcessing = false;
			this.successMessage = 'Your availability changes have been saved.';
			this.$store.commit('availability/clearSelectedDates');

			setTimeout(() => {
				this.successMessage = null;
			}, 1000);
		},
	},
	destroyed() {
		this.$store.commit('availability/clearSelectedDates');
	},
};
</script>
